import { format } from 'date-fns';
import { Badge } from '@/components/ui/Badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  formatCurrency,
  formatDuration,
  stripHtml,
  capitalize,
} from '@/utils/formatters';
import { formatDisplayDate } from '@/utils/date';
import type { ColumnKey } from '@/features/reports/components/ColumnSelector';
import type { ReportTask } from '@/features/reports/utils/exportToExcel';
import { NO_PROJECT_COLOR } from '@/utils/projectColor';

interface ReportTableProps {
  entries: readonly ReportTask[];
  visibleColumns: ColumnKey[];
}

const COLUMN_LABELS: Record<ColumnKey, string> = {
  date: 'Date',
  member: 'Member',
  title: 'Title',
  description: 'Description',
  project: 'Project',
  duration: 'Duration',
  hourlyRate: 'Hourly Rate',
  totalAmount: 'Value',
  billingStatus: 'Billing Status',
};

function getStatusBadge(status: string) {
  const variants: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
    unbilled: 'warning',
    billed: 'success',
  };
  return (
    <Badge variant={variants[status] ?? 'default'}>
      {capitalize(status)}
    </Badge>
  );
}

function renderCell(entry: ReportTask, column: ColumnKey): React.ReactNode {
  switch (column) {
    case 'date':
      return formatDisplayDate(entry.date);
    case 'member':
      return entry.members.map((m) => m.name).join(', ');
    case 'title':
      return entry.title;
    case 'description':
      return stripHtml(entry.description) || '—';
    case 'project':
      // Small color dot before the project name. Project-less entries
      // (issue #10) fall back to the brand primary so they're visually
      // consistent with the rest of the UI.
      return (
        <span className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: entry.project?.color ?? NO_PROJECT_COLOR }}
            aria-hidden="true"
          />
          {entry.project?.name ?? 'No project'}
        </span>
      );
    case 'duration':
      return formatDuration(entry.durationMinutes);
    case 'hourlyRate':
      return formatCurrency(entry.hourlyRate, entry.currency);
    case 'totalAmount':
      return formatCurrency(entry.totalAmount, entry.currency);
    case 'billingStatus':
      return getStatusBadge(entry.billingStatus);
  }
}

export function ReportTable({ entries, visibleColumns }: ReportTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {visibleColumns.map((column) => (
            <TableHead key={column}>{COLUMN_LABELS[column]}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.length === 0 && (
          <TableRow>
            <TableCell
              colSpan={visibleColumns.length}
              className="h-24 text-center text-muted-foreground"
            >
              No entries match the selected filters.
            </TableCell>
          </TableRow>
        )}
        {entries.map((entry) => (
          <TableRow key={entry.id}>
            {visibleColumns.map((column) => (
              <TableCell key={`${entry.id}-${column}`}>{renderCell(entry, column)}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
